import React, { useMemo, useRef, useState } from "react";
import * as d3 from "d3";
import { useColorMode } from "@chakra-ui/system";

/** Compute Tukey boxplot statistics for one group */
function boxStats(values) {
  const v = values.filter((x) => Number.isFinite(x)).sort(d3.ascending);
  if (v.length === 0) return null;

  const q1 = d3.quantileSorted(v, 0.25);
  const med = d3.quantileSorted(v, 0.5);
  const q3 = d3.quantileSorted(v, 0.75);
  const iqr = q3 - q1;

  const loFence = q1 - 1.5 * iqr;
  const hiFence = q3 + 1.5 * iqr;

  // whiskers inside fences
  const loWhisker = v.find((x) => x >= loFence) ?? v[0];
  const hiWhisker =
    [...v].reverse().find((x) => x <= hiFence) ?? v[v.length - 1];

  const outliers = v.filter((x) => x < loFence || x > hiFence);
  const mean = d3.mean(v);
  //   console.log("outlier", outliers);

  return {
    q1,
    med,
    q3,
    iqr,
    loWhisker,
    hiWhisker,
    outliers,
    mean,
    n: v.length,
  };
}

/**
 * BoxPlot component
 *
 * props.datasets: [{ label, values: number[], color?: string }]
 * props.title: string | null
 * props.annotation: optional string (can contain '\n') rendered in a rounded box below the axis
 */
export default function BoxPlot({
  datasets = [],
  width = 180,
  height = 256,
  margin = { top: 48, right: 20, bottom: 40, left: 40 },
  title = null,
  titleColor = "black",
  axisFontSize = 13,
  boxWidth = 26,
  strokeWidth = 1.5,
  showMean = true, // draw mean as a triangle
  showOutliers = true, // draw outlier markers
  colors = [], // optional palette fallback
  annotation = null, // string; use '\n' for multiple lines
  annotationTextColor = "black",
  annotationBg = "white",
  annotationBorder = "#333",
  annotationRadius = 8,
  bandPadding = 1.25,
  fmt = d3.format(".2f"),
}) {
  const { colorMode, toggleColorMode } = useColorMode();
  const axesColor = colorMode === "dark" ? "#fff" : "#000";
  const whiskerColor = colorMode === "dark" ? "#b8b7b7ff" : "#1f1f1f";
  const gridColor = colorMode === "dark" ? "#acacacff" : "#3d3d3dff";

  const containerRef = useRef(null);
  const [tip, setTip] = useState(null); // {x,y,html}

  // Stats per group
  const stats = useMemo(
    () =>
      datasets.map((d, i) => ({
        label: d.label ?? `G${i + 1}`,
        color: d.color ?? colors[i % colors.length] ?? "#f0c36d",
        ...boxStats(d.data),
      })),
    [datasets, colors]
  );

  // y-domain from min whisker to max whisker (or data)
  const yMin = d3.min(stats, (s) =>
    s ? Math.min(s.loWhisker, s.q1) : Infinity
  );
  const yMax = d3.max(stats, (s) =>
    s ? Math.max(s.hiWhisker, s.q3) : -Infinity
  );
  const pad = (yMax - yMin || 1) * 0.05;

  const x = d3
    .scaleBand()
    .domain(datasets.map((d, i) => d.label ?? `G${i + 1}`))
    .range([margin.left, width - margin.right])
    .padding(Math.max(0, bandPadding));

  const y = d3
    .scaleLinear()
    .domain([yMin - pad, yMax + pad])
    .nice()
    .range([height - margin.bottom, margin.top]);
  // Use on the y axis:

  // helpers
  const centerX = (label) => x(label) + x.bandwidth() / 2;

  // Annotation layout
  const annotationLines = annotation ? String(annotation).split("\n") : [];
  const annBoxWidth = Math.min(0.9 * (width - margin.left - margin.right), 160);
  const annBoxHeight = annotation ? 18 + annotationLines.length * 16 : 0;

  // Tooltip helpers
  function showTooltip(evt, s) {
    if (!s) return;
    const bounds = containerRef.current.getBoundingClientRect();
    const px = evt.clientX - bounds.left + 10; // small offset
    const py = evt.clientY - bounds.top - 10;

    const html = `
      <div><strong>${s.label}</strong> (n=${s.n})</div>
      ${showMean ? `<div>Mean: ${fmt(s.mean)}</div>` : ""}
      <div>Median: ${fmt(s.med)}</div>
      <div>Q1: ${fmt(s.q1)}</div>      
      <div>Q3: ${fmt(s.q3)}</div>
      <div>IQR: ${fmt(s.iqr)}</div>
      <div>Whiskers: [${fmt(s.loWhisker)}, ${fmt(s.hiWhisker)}]</div>
      ${
        showOutliers && s.outliers?.length
          ? `<div>Outliers: ${s.outliers.length}</div>`
          : ""
      }
    `;
    setTip({ x: px, y: py, html });
  }

  function hideTooltip() {
    setTip(null);
  }

  return (
    <div ref={containerRef} style={{ position: "relative", width, height }}>
      <svg width={width} height={height}>
        {/* Title */}
        {title && (
          <text
            x={width / 2}
            y={margin.top / 2}
            textAnchor="middle"
            fontWeight="bold"
            fill={axesColor}
            style={{ fontSize: axisFontSize + 2 }}
          >
            {title}
          </text>
        )}

        {/* Gridlines */}
        <g opacity={0.75}>
          {y.ticks(5).map((t, i) => (
            <line
              key={i}
              x1={margin.left}
              x2={width - margin.right}
              y1={y(t)}
              y2={y(t)}
              stroke={gridColor}
              strokeDasharray="4,4"
            />
          ))}
        </g>

        {/* Boxes */}
        {stats.map((s, i) => {
          if (!s) return null;
          const cx = centerX(s.label);
          // const bw = Math.min(boxWidth, x.bandwidth() * 0.9);
          const bw = boxWidth;
          const x0 = cx - bw / 2;

          return (
            <g
              key={i}
              onMouseMove={(e) => showTooltip(e, s)}
              onMouseEnter={(e) => showTooltip(e, s)}
              onMouseLeave={hideTooltip}
              style={{ cursor: "default" }}
            >
              {/* Whiskers */}
              <line
                x1={cx}
                x2={cx}
                y1={y(s.loWhisker)}
                y2={y(s.q1)}
                stroke={whiskerColor}
                strokeWidth={strokeWidth}
              />
              <line
                x1={cx}
                x2={cx}
                y1={y(s.q3)}
                y2={y(s.hiWhisker)}
                stroke={whiskerColor}
                strokeWidth={strokeWidth}
              />
              {/* Whisker caps */}
              <line
                x1={x0}
                x2={x0 + bw}
                y1={y(s.loWhisker)}
                y2={y(s.loWhisker)}
                stroke={whiskerColor}
                strokeWidth={strokeWidth}
              />
              <line
                x1={x0}
                x2={x0 + bw}
                y1={y(s.hiWhisker)}
                y2={y(s.hiWhisker)}
                stroke={whiskerColor}
                strokeWidth={strokeWidth}
              />
              {/* Box (Q1–Q3) */}
              <rect
                x={x0}
                y={y(s.q3)}
                width={bw}
                height={Math.max(1, y(s.q1) - y(s.q3))}
                fill={s.color}
                fillOpacity={0.42}
                stroke={whiskerColor}
                strokeWidth={strokeWidth}
                rx={0}
              />
              {/* Median */}
              <line
                x1={x0}
                x2={x0 + bw}
                y1={y(s.med)}
                y2={y(s.med)}
                stroke="#e53935"
                strokeWidth={strokeWidth}
              />
              {/* Mean (triangle) */}
              {showMean && (
                <path
                  d={`M ${cx} ${y(s.mean) - 6} L ${cx - 7} ${y(s.mean) + 6} L ${
                    cx + 7
                  } ${y(s.mean) + 6} Z`}
                  fill="#369e3bff"
                  stroke="none"
                  opacity={0.95}
                />
              )}
              {/* Outliers */}
              {showOutliers &&
                s.outliers?.map((v, j) => (
                  <circle
                    key={j}
                    cx={cx}
                    cy={y(v)}
                    r={2}
                    fill={axesColor}
                    opacity={0.8}
                  />
                ))}
            </g>
          );
        })}

        {/* Axes */}
        <g
          className="x-axis"
          transform={`translate(0,${height - margin.bottom})`}
          fill={axesColor}
        >
          {/* ticks */}
          {datasets.map((d, i) => {
            const label = d.label ?? `G${i + 1}`;
            const cx = centerX(label);
            return (
              <g key={i} transform={`translate(${cx},0)`}>
                <line y1={0} y2={6} stroke={axesColor} />
                <text
                  dy="1.4em"
                  textAnchor="middle"
                  style={{ fontSize: axisFontSize }}
                >
                  {label}
                </text>
              </g>
            );
          })}
          {/* baseline */}
          <line
            x1={margin.left}
            x2={width - margin.right}
            y1={0}
            y2={0}
            stroke={axesColor}
            strokeWidth={1.2}
          />
        </g>

        <g className="y-axis" transform={`translate(${margin.left},0)`}>
          {y.ticks(5).map((t, i) => (
            <g key={i} transform={`translate(0,${y(t)})`} fill={axesColor}>
              <line x1={0} x2={-6} stroke={axesColor} />
              <text
                x={-9}
                dy="0.35em"
                textAnchor="end"
                style={{ fontSize: axisFontSize }}
              >
                {d3.format("~s")(t)}
              </text>
            </g>
          ))}
          {/* axis line */}
          <line
            x1={0}
            x2={0}
            y1={height - margin.bottom}
            y2={margin.top}
            stroke={axesColor}
            strokeWidth={1.2}
          />
        </g>

        {/* Annotation box below the axis */}
        {annotation && (
          <g
            transform={`translate(${width / 2 - annBoxWidth / 2}, ${
              height - margin.bottom + 10
            })`}
          >
            <rect
              width={annBoxWidth}
              height={annBoxHeight}
              rx={annotationRadius}
              ry={annotationRadius}
              fill={annotationBg}
              stroke={annotationBorder}
            />
            {annotationLines.map((line, i) => (
              <text
                key={i}
                x={annBoxWidth / 2}
                y={12 + i * 16}
                textAnchor="middle"
                fill={annotationTextColor}
                style={{ fontSize: axisFontSize }}
              >
                {line}
              </text>
            ))}
          </g>
        )}
      </svg>

      {/* Tooltip */}
      {tip && (
        <div
          style={{
            position: "absolute",
            left: tip.x,
            top: tip.y,
            pointerEvents: "none",
            background: "rgba(255,255,255,0.95)",
            border: "1px solid #333",
            borderRadius: 6,
            padding: "6px 8px",
            boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
            fontSize: 14,
            whiteSpace: "nowrap",
            color: "black",
            zIndex: 1000,
          }}
          dangerouslySetInnerHTML={{ __html: tip.html }}
        />
      )}
    </div>
  );
}
